import type { Temporal } from "@js-temporal/polyfill";
import { WORLD_SEED } from "../content/scale";

export type WeatherCategory = "clear" | "cloud" | "rain" | "snow" | "fog" | "storm";
export interface WeatherState {
  category: WeatherCategory;
  cloud: number;
  fog: number;
  precipitation: number;
  blend: number;
}

type Weights = Partial<Record<WeatherCategory, number>>;
const SEASON_WEIGHTS: Record<"winter" | "spring" | "summer" | "autumn", Weights> = {
  winter: { clear: 0.25, cloud: 0.3, rain: 0.15, snow: 0.25, fog: 0.05 },
  spring: { clear: 0.35, cloud: 0.25, rain: 0.3, fog: 0.08, storm: 0.02 },
  summer: { clear: 0.5, cloud: 0.2, rain: 0.15, storm: 0.12, fog: 0.03 },
  autumn: { clear: 0.4, cloud: 0.25, rain: 0.2, fog: 0.1, storm: 0.05 },
};

function hashUnit(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) / 0x1_0000_0000;
}

function weightedSample(weights: Weights, unit: number): WeatherCategory {
  let accumulated = 0;
  for (const [category, weight] of Object.entries(weights) as [WeatherCategory, number][]) {
    accumulated += weight;
    if (unit < accumulated) return category;
  }
  return "clear";
}

function seasonForMonth(month: number): keyof typeof SEASON_WEIGHTS {
  if (month === 12 || month <= 2) return "winter";
  if (month <= 5) return "spring";
  if (month <= 8) return "summer";
  return "autumn";
}

export class WeatherService {
  readonly #months = new Map<string, WeatherCategory[]>();

  stateAt(zoned: Temporal.ZonedDateTime): WeatherState {
    const monthKey = `${zoned.year}-${String(zoned.month).padStart(2, "0")}`;
    let slots = this.#months.get(monthKey);
    if (!slots) {
      slots = [];
      const weights = SEASON_WEIGHTS[seasonForMonth(zoned.month)];
      for (let index = 0; index < 31 * 8; index += 1) {
        const random = hashUnit(`${WORLD_SEED}|${monthKey}|${index}`);
        const previous = slots[index - 1];
        slots.push(
          index > 0 && previous && random < 0.7
            ? previous
            : weightedSample(weights, index > 0 ? (random - 0.7) / 0.3 : random),
        );
      }
      this.#months.set(monthKey, slots);
    }
    const slot = (zoned.day - 1) * 8 + Math.floor(zoned.hour / 3);
    const category = slots[slot] ?? "clear";
    const blend = Math.min(1, zoned.minute / 10);
    const cloud = category === "clear" ? 0.08 : category === "cloud" ? 0.75 : 0.9;
    const fog = category === "fog" ? 0.85 : category === "storm" ? 0.25 : 0.08;
    const precipitation =
      category === "rain" || category === "snow" ? 0.75 : category === "storm" ? 1 : 0;
    return { category, cloud, fog, precipitation, blend };
  }
}
